import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { NoRecordsFoundMessage } from './NoRecordsFoundMessage';
import { PaginationMUI } from '../pagination/PaginationMUI';
import '../../styles/sass/global/components/pagination.scss';
import { Typography } from '@mui/material';
import { PaginationToolbar } from '../pagination';
import { useTranslation } from 'react-i18next';
import { palette } from '../../styles/theme';

const defaultClasses = 'table-head-custom table-vertical-center overflow-hidden';
const defaultWrapperClasses = 'table-responsive';
const defaultKeyField = 'id';
const defaultSorted = [{ dataField: 'id', order: 'desc' }];
const defaultSizePerPageList = [
    { text: '10', value: 10 },
    { text: '25', value: 25 },
    { text: '50', value: 50 },
    { text: '100', value: 100 },
];
const defaultPaginationOptions = {
    sizePerPageList: defaultSizePerPageList,
    sizePerPage: Number(localStorage.getItem('sizePerPage')) || 10,
    page: 1,
};

/**
 * Adapts bootstrap-react-table column definitions to MUI DataGrid column definitions.
 * Supports: dataField (incl. dot-paths), key, text (string or JSX), formatter,
 * formatExtraData, sort, align, headerAlign, classes, headerClasses.
 */
function adaptColumns (bootstrapColumns) {
    const fieldToDataField = {};
    const dataFieldToField = {};

    const dataGridColumns = bootstrapColumns.map((col, index) => {
        const rawField = col.dataField || `__field_${index}__`;
        const isDotPath = rawField.includes('.');

        // Use the column field (col.key if provided for duplicate dataFields)
        const field = col.key || (isDotPath ? rawField.replace(/\./g, '_') : rawField);

        // valueGetter needed when field diverges from the actual data path
        const needsValueGetter = isDotPath || field !== rawField;

        // Build bidirectional mapping for server-side sorting keeps using legacy bootstrap dataField
        fieldToDataField[field] = rawField;
        if (dataFieldToField[rawField] === undefined) {
            dataFieldToField[rawField] = field;
        }

        // Smart flex strategy: balance content-driven sizing with constraints.
        // Goal: columns grow with content, but small-content cols don't explode.
        //
        // New approach: Default to generous, let columns that KNOW they need less specify it.
        // - Has formatter or formatExtraData (renders components): needs space → 1.2
        // - ID: known to be short → 0.5 (BUT respect explicit width if set)
        // - Plain text (descriptions, reasons): needs room → 1.5
        // - Default: balanced share → 1
        //
        // This mimics bootstrap-table behavior: content determines width naturally.
        const hasFormatterOrActions = Boolean(col.formatter || col.formatExtraData);
        const isId = rawField === 'id';
        const isPlainText = !col.formatter && !col.formatExtraData && rawField !== 'id';

        let flex = 1;
        if (isPlainText) flex = 1.5; // Descriptions, reasons need room to show
        else if (hasFormatterOrActions) flex = 1.2; // Components need space
        else if (isId) flex = 0.5; // IDs are short numbers

        // Developer override (applied AFTER default flex is calculated)
        if (col.flex !== undefined) {
            flex = col.flex;
        }

        const minWidth = 70;

        const dgCol = {
            field,
            headerName: typeof col.text === 'string' ? col.text : '',
            sortable: col.sort === true,
            align: col.align || 'left',
            headerAlign: col.headerAlign || 'left',
            cellClassName: col.classes || '',
            headerClassName: col.headerClasses || '',
            disableColumnMenu: true,
        };

        // Respect explicit width if set (pixel value), otherwise use flex
        if (col.width) {
            dgCol.width = col.width;
        }
        else {
            dgCol.flex = flex;
            dgCol.minWidth = minWidth;
        }

        // JSX header (e.g. <CodebarModal />)
        if (typeof col.text !== 'string') {
            dgCol.renderHeader = () => col.text;
        }

        // DataGrid v6: valueGetter({ value, row, field, ... })
        if (needsValueGetter) {
            dgCol.valueGetter = (params) =>
                rawField.split('.').reduce((obj, key) => obj?.[key], params.row);
        }

        // bootstrap-table formatter(cellContent, row, rowIndex, formatExtraData)
        if (col.formatter) {
            dgCol.renderCell = (params) => {
                const rowIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
                return col.formatter(params.value, params.row, rowIndex, col.formatExtraData);
            };
        }

        return dgCol;
    });

    return { dataGridColumns, fieldToDataField, dataFieldToField };
}

export function Table ({
    data = [],
    columns = [],
    pagination = {},
    total,
    classes = defaultClasses,
    wrapperClasses = defaultWrapperClasses,
    sort = defaultSorted,
    keyField = defaultKeyField,
    isLoading = true,
    onChange = _.noop,
}) {
    const [sortModel, setSortModel] = useState([]);
    const { t } = useTranslation();

    const rows = data || [];
    const paginationOpts = _.defaultsDeep({}, pagination, defaultPaginationOptions);
    const sizePerPage = Number(paginationOpts.sizePerPage) || defaultPaginationOptions.sizePerPage;
    const currentPage = Number(paginationOpts.page) || 1;
    const totalPages = Math.ceil((total || 0) / sizePerPage);

    const { dataGridColumns, fieldToDataField, dataFieldToField } = useMemo(
        () => adaptColumns(columns),
        [columns]
    );

    // Initialize DataGrid sortModel from legacy bootstrap sort config.
    // NOTE: Most callers pass a constant `sort` (e.g. defaultSorted) and do NOT
    // re-render it when the user changes sorting. So we must NOT treat `sort`
    // as a controlled source of truth, otherwise we would reset the user's sort.
    useEffect(() => {
        setSortModel((prev) => {
            if (prev.length > 0) return prev;

            const next = (sort || []).map((s) => ({
                field: dataFieldToField[s.dataField] ?? s.dataField,
                sort: s.order,
            }));

            return next;
        });
    }, [sort, dataFieldToField]);

    const handleSortModelChange = useCallback((model) => {
        setSortModel(model);

        if (model.length > 0) {
            const gridField = model[0].field;
            const legacySortField = fieldToDataField[gridField] ?? gridField;
            onChange(prev => ({ ...prev, sortField: legacySortField, sortOrder: model[0].sort }));
        }
    }, [onChange, fieldToDataField]);

    const handlePageChange = useCallback((newPage) => {
        onChange(prev => ({ ...prev, pageNumber: newPage }));
    }, [onChange]);

    const handleSizePerPageChange = useCallback((newSize) => {
        onChange(prev => ({ ...prev, pageNumber: 1, pageSize: newSize }));
    }, [onChange]);

    return (
        <div className={wrapperClasses} style={{ maxWidth: '99%' }}>
            <DataGrid
                className={classes}
                rows={rows}
                columns={dataGridColumns}
                getRowId={(row) => row[keyField]}
                loading={isLoading}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                hideFooter
                disableRowSelectionOnClick
                autoHeight
                density="compact"
                showCellVerticalBorder
                showColumnVerticalBorder
                getRowHeight={() => 'auto'}
                getRowClassName={({ indexRelativeToCurrentPage }) =>
                    indexRelativeToCurrentPage % 2 !== 0 ? 'DataGrid-striped-row' : ''
                }
                slots={{ noRowsOverlay: () => null }}
                sx={{
                    border: 'none',
                    borderRadius: 0,
                    boxShadow: 'none',

                    // Ensure nested components inherit smaller table typography
                    fontSize: '0.923rem', // 12px @ 1rem=13px

                    // Disable transitions/animations so columns render with exact size without growing dynamically
                    '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeaders *, & .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader *, & .MuiDataGrid-cell, & .MuiDataGrid-cell *, & .MuiDataGrid-row': {
                        transition: 'none !important',
                        animation: 'none !important',
                    },

                    // Typography + spacing parity (bootstrap tables)
                    '& .MuiDataGrid-columnHeaderTitle': {
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        color: palette.primary.main,
                    },
                    '& .MuiDataGrid-columnHeader': {
                        // Matches bootstrap header vertical rhythm (1rem top/bottom)
                        paddingTop: '1rem',
                        paddingBottom: '1rem',
                    },
                    '& .MuiDataGrid-cell': {
                        fontSize: '0.923rem', // 12px @ 1rem=13px
                        fontWeight: 400,
                        color: palette.primary.main,
                        // Slightly tighter than default DataGrid to resemble bootstrap tables
                        paddingTop: '0.5rem',
                        paddingBottom: '0.5rem',
                        // Ensure clickable elements work inside cells
                        pointerEvents: 'auto',
                    },

                    // Hover + stripes
                    '& .MuiDataGrid-row:hover': { backgroundColor: palette.disabled[50] },
                    '& .DataGrid-striped-row': { backgroundColor: palette.beige[50] },

                    // Override DataGrid default outline/border radius
                    '& .MuiDataGrid-root': {
                        borderRadius: 0,
                        border: 'none',
                    },
                }}
            />
            {!isLoading && rows.length === 0 && (
                <NoRecordsFoundMessage entities={rows} />
            )}
            {!isLoading && (
                <div className='paginationProps'>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', width: '100%' }}>
                        <div style={{ gridColumn: '2' }} className='d-flex align-items-center justify-content-center'>
                            <PaginationToolbar
                                sizePerPageList={paginationOpts.sizePerPageList}
                                sizePerPage={sizePerPage}
                                onSizePerPageChange={handleSizePerPageChange}
                            />
                            <Typography sx={{ marginLeft: '0.769rem' }} className='totalClients'>
                                {t('pagination.of')} {total}
                            </Typography>
                        </div>
                        <div style={{ gridColumn: '3' }} className='paginationItems'>
                            <PaginationMUI
                                totalSize={total}
                                totalPages={totalPages}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Set display names for debugging.
if (process.env.NODE_ENV !== 'production') {
    Table.displayName = 'Table';
}
